import { createSelector } from '@reduxjs/toolkit';

import { STATIC_TABLE_SETTINGS } from 'store/reducers/constants/constants.js';
import { getEffectiveTableSettings } from 'store/functions/tableSettingsOverrides';
import { travelTimeEstimateSummary } from 'store/functions/travelCostResults';
import { missingRequired, missingRequiredMessage } from 'store/functions/missingRequired';
import { minRowsMessage, rowIsValid, setupChecklist } from 'store/functions/setupChecklist';
const inputFunctions = require('store/functions/inputFunctions.js');
const staticModelSettings = require('store/data/model_configs.json');

// Shared stable fallbacks. Selectors must never manufacture a fresh
// object/array for the "empty" case — useSelector compares by reference, so a
// per-call literal re-renders its subscribers on every store dispatch (the
// solve-status poller dispatches every 750ms–5s for the length of a run).
const EMPTY_TABLE = Object.freeze([]);

/**
 * @fileoverview Table-related Redux selectors.
 *
 * This file centralizes selectors used to derive table configuration and metadata from
 * application state, including operations like determining which tables/columns are
 * available, enabled, and toggleable for the currently active model context.
 */


/**
 * Selector to get the length of a specified table from the input data in state.
 *
 * @param {string} tableName - The name of the table whose length is to be retrieved.
 * @returns {(state: Object) => number} A selector function that returns the length of the specified table,
 * or 0 if the table does not exist in the input data.
 */
export const selectTableLength = (tableName) => (state) => {
  if (!tableName) return 0;

  const table = state?.inputData?.present?.data?.[tableName];
  return Array.isArray(table) ? table.length : 0;
};


/**
 * Creates a selector that returns toggleable columns for each enabled input table for a given model type.
 *
 * The selector inspects the currently active model in state, filters to enabled input tables
 * (excluding those only enabled with output), and then collects column names marked as toggleable
 * and having a `correspondingColumn` mapping in static table settings.
 *
 * @param {string} modelType - The model type to scope the selector; if falsy, the selector returns an empty object.
 * @returns {(state: Object) => Record<string, string[]>} A memoization-free selector function that, when invoked with state,
 * returns an object mapping table names to arrays of toggleable column names.
 */
export const selectToggleableColumns = (modelType) => (state) => {
  //gets the columns for the input model that can be toggled on/off;
  const activeModelType = modelType || state?.inputData?.present?.activeModelType;
  if (!activeModelType) return {};

  const enabledTables = staticModelSettings?.[activeModelType]?.input?.enabled;
  const enabledOnlyWithOutput = staticModelSettings?.[activeModelType]?.input?.enabledOnlyWithOutput;

  if (!enabledTables || typeof enabledTables !== 'object') return {};

  const inputTables = [];
  for (const [tableName, enabled] of Object.entries(enabledTables)) {
    if (enabled && !enabledOnlyWithOutput?.[tableName]) {
      inputTables.push(tableName);
    }
  }

  const toggleableColumns = {};
  inputTables.forEach((tableName) => {
    const isToggleable = STATIC_TABLE_SETTINGS?.[tableName]?.isToggleable;
    if (!isToggleable || typeof isToggleable !== 'object') return;

    const tableToggleableColumns = [];
    for (const columnName of Object.keys(isToggleable)) {
      if (
        isToggleable?.[columnName]?.correspondingColumn &&
        isToggleable?.[columnName]?.isPrimary === true
      ) {
        tableToggleableColumns.push(columnName);
      }
    }

    if (tableToggleableColumns.length > 0) {
      toggleableColumns[tableName] = [...tableToggleableColumns];
    }
  });

  return toggleableColumns;
};


/**
 * Validates a table data set by checking each row's `Valid` flag and collecting
 * identifiers/indices for invalid rows.
 *
 * Improvements recommended:
 * - Prefer `const`/`let` over `var`.
 * - Use a single pass with `reduce` (or `for...of`) to accumulate results.
 * - Treat the `Valid` field strictly (`elem.Valid === true`) or normalize missing values.
 * - Clearly define 1-based indices (current behavior) in the contract.
 *
 * @typedef {Object} TableRow
 * @property {string} UUID - Unique identifier for the row.
 * @property {boolean} Valid - Whether the row is valid.
 * @property {any} [key] - Additional row properties.
 *
 * @typedef {Object} CheckTableValidResult
 * @property {boolean} valid - `true` if no invalid rows were found; otherwise `false`.
 * @property {string[]} invalidRows - UUIDs for rows where `Valid` is falsy.
 * @property {number[]} invalidRowIndices - 1-based indices corresponding to invalid rows.
 *
 * @param {TableRow[]} tableData - Array of table rows to validate.
 * @returns {CheckTableValidResult} Validation summary including invalid UUIDs and 1-based indices.
 */
const checkTableValid = (tableData) => {
  const invalidRows = [];
  const invalidRowIndices = [];

  if (!Array.isArray(tableData)) {
    return { 'valid': true, 'invalidRows': invalidRows, 'invalidRowIndices': invalidRowIndices };
  }

  tableData.forEach((elem, idx) => {
    // The rule itself lives in store/functions/setupChecklist, because the setup
    // checklist applies it to the same rows and a tick there has to mean what a
    // refusal here means.
    if (!rowIsValid(elem)) {
      invalidRows.push(elem?.UUID ?? String(idx));
      invalidRowIndices.push(idx + 1);
    }
  });

  if (invalidRows.length === 0) {
    return { 'valid': true, 'invalidRows': invalidRows, 'invalidRowIndices': invalidRowIndices };
  }

  return { 'valid': false, 'invalidRows': invalidRows, 'invalidRowIndices': invalidRowIndices };
};


/**
 * Validates the current dashboard input tables for the active model type.
 *
 * Iterates over `state.inputData.present.data` and checks only tables that are enabled
 * for the active model type, are not result tables, and exist in `STATIC_TABLE_SETTINGS`.
 * A table is considered invalid if it has fewer than the configured minimum rows or if
 * `checkTableValid(tableData)` reports invalid rows.
 *
 * @param {object} state - Redux state object.
 * @param {object} state.inputData - Input data slice of state.
 * @param {object} state.inputData.present - Present (current) state for input data.
 * @param {string} state.inputData.present.activeModelType - The currently selected model type key.
 * @param {Record<string, any[]>} state.inputData.present.data - Map of table names to table row arrays.
 * @returns {{valid: boolean, invalidTables: string[], invalidMessages: string[]}} Validation result:
 * `valid` indicates whether all checked tables are valid; `invalidTables` lists invalid table keys;
 * `invalidMessages` contains human-readable messages describing validation failures.
 */
// Memoized: this walks every row of every enabled input table and returns a
// fresh result object, and it feeds the always-mounted Optimize button. Without
// memoization it re-ran — and re-rendered the button — on every store dispatch,
// including the solve-status poll every few seconds (see the map-OOM fix in
// 617994ed6 for the pattern). `data` and `activeModelType` are the only inputs,
// so it now recomputes only when a table actually changes.
export const selectDashboardIsValid = createSelector(
  [
    (state) => state.inputData.present.data,
    (state) => state.inputData.present.activeModelType,
    // The messages name tables by their effective titles, so a scenario
    // rename (generalSettings.tableLabels) must invalidate the memo too.
    (state) => state.inputData.present.generalSettings?.tableLabels,
    // The headers in the message differ on an x-y scenario (X/Y, not
    // Latitude/Longitude), so the coordinate system has to invalidate the memo.
    (state) => state.inputData.present.generalSettings?.mapping?.coordinateSystem,
  ],
  (data, activeModelType, tableLabels, coordinateSystem) => {
    const invalidTables = [];
    const invalidMessages = [];

    const modelSettings = staticModelSettings;

    const isEnabledInputTable = (tableName) =>
        Boolean(modelSettings[activeModelType].input.enabled[tableName]);

    const getMinRows = (tableName) =>
        modelSettings[activeModelType].input.minimumRows?.[tableName] || 0;

    const addInvalid = (tableName, message) => {
        invalidTables.push(tableName);
        invalidMessages.push(message);
    };

    for (const [tableName, tableData] of Object.entries(data)) {
        if (!tableData || !isEnabledInputTable(tableName)) continue;

        const tableSettings = STATIC_TABLE_SETTINGS[tableName];
        if (!tableSettings || tableSettings.isResultTable) continue;

        const tableSchema = getEffectiveTableSettings(tableName, coordinateSystem, tableLabels);
        const tableTitle = tableSchema.title;
        const minRows = getMinRows(tableName);

        if (minRows && tableData.length < minRows) {
            addInvalid(tableName, minRowsMessage(tableTitle, minRows));
            continue;
        }

        const tableValidResponse = checkTableValid(tableData);
        if (!tableValidResponse.valid) {
            /* Say what is wrong, not only where. "See row numbers: 1, 2, 3 … 13"
               plus the word "invalid" tells someone where to look and nothing
               about what to do — and the person reading it has usually done
               nothing worse than leave a cell blank. When the whole story is
               missing required fields, name the fields; otherwise fall back to
               the indices, which still beat silence for a dropdown or
               time-window problem. */
            const missing = missingRequired(tableData, tableSchema);
            const explained = missing.rowsAffected === tableValidResponse.invalidRowIndices.length
                ? missingRequiredMessage(missing, tableTitle)
                : '';
            addInvalid(
                tableName,
                explained
                    || `${tableTitle} table is invalid. See row numbers: ${tableValidResponse.invalidRowIndices.join(', ')}`
            );
        }
    }

    return {
        valid: invalidTables.length === 0,
        invalidTables,
        invalidMessages,
    };
  }
);

/**
 * "What do I still need to add?" — one entry per table this model cannot run
 * without, each carrying its own settled/unsettled state.
 *
 * Composed on `selectDashboardIsValid` rather than recomputing anything, so the
 * checklist and the Run button's refusal are two presentations of one answer: a
 * tick means the validator has no objection to that table, and an unsettled
 * entry quotes the validator's own sentence. Building this from row counts alone
 * would tick for a vehicle that exists and cannot be used, one screen before Run
 * says so — see store/functions/setupChecklist for why that is the live trap.
 *
 * Memoized on the same four inputs as the validator it composes (plus the
 * validator's own memoized result), so it holds one array reference across every
 * unrelated dispatch — the solve-status poll runs every few seconds and this
 * feeds an always-mounted component.
 */
export const selectSetupChecklist = createSelector(
  [
    selectDashboardIsValid,
    (state) => state.inputData.present.data,
    (state) => state.inputData.present.activeModelType,
    (state) => state.inputData.present.generalSettings?.tableLabels,
    (state) => state.inputData.present.generalSettings?.mapping?.coordinateSystem,
  ],
  (validity, data, activeModelType, tableLabels, coordinateSystem) => setupChecklist({
    data,
    activeModelType,
    tableLabels,
    coordinateSystem,
    invalidTables: validity.invalidTables,
    invalidMessages: validity.invalidMessages,
  })
);

/**
 * Selector that returns unique combinations of values across the given columns for a table.
 *
 * Uniqueness is determined by the tuple of `columnNames` values per row.
 *
 * @param {{ tableName: string, columnNames: string[] }} params
 * @returns {(state: object) => Record<string, any>[]} Array of objects containing the selected column values.
 */
export const selectUniqueColumnValues =
    ({ tableName, columnNames = [] }) =>
    (state) => {
        if (!tableName || columnNames.length === 0) return [];

        const table = state?.inputData?.present?.data?.[tableName];
        if (!Array.isArray(table) || table.length === 0) return [];

        const seen = new Set();
        const results = [];

        for (const row of table) {
            const values = columnNames.map((col) => row?.[col]);
            const key = JSON.stringify(values);

            if (seen.has(key)) continue;
            seen.add(key);

            results.push(
                columnNames.reduce((acc, col, idx) => {
                    acc[col] = values[idx];
                    return acc;
                }, {})
            );
        }

        return results;
    };


/**
 * Creates a selector that checks whether the specified table in the input data is empty.
 *
 * @param {string} tableName - The key/name of the table within `state.inputData.present.data`.
 * @returns {(state: Object) => boolean} A selector function that returns `true` if the table has no rows; otherwise `false`.
 */
export const selectTableIsEmpty = (tableName) => (state) => {
  if (!tableName || typeof tableName !== 'string') {
    throw new Error(`selectTableIsEmpty: tableName must be a non-empty string (received: ${String(tableName)})`);
  }

  const table = state?.inputData?.present?.data?.[tableName];

  if (!Array.isArray(table)) {
    throw new Error(`selectTableIsEmpty: invalid tableName "${tableName}" (table not found or not an array)`);
  }

  return table.length === 0;
};


/**
 * Selects the drops table name associated with the currently active model.
 *
 * Reads `state.inputData.present.activeModelType`, looks up its configuration in
 * `staticModelSettings`, and returns the configured `dropsTable` value.
 *
 * @param {Object} state - The Redux application state.
 * @returns {*} The drops table identifier/name for the active model (as defined in `staticModelSettings`).
 */
export const selectDropsTable = state => {
  const activeModelType = state.inputData.present.activeModelType;
  const activeModelSettings = staticModelSettings[activeModelType];
  const dropsTable = activeModelSettings.dropsTable;
  return dropsTable;
}

/**
 * Selector that determines whether a given column is viewable for a table.
 *
 * Reads `state.inputData.present.tableSettings[tableName].isViewable[columnName]`.
 *
 * Conventions/behavior:
 * - If `isViewable` is explicitly `true` or `false`, that value is returned.
 * - If the setting is missing (table/column not configured), this selector defaults to `true`
 *   (i.e., columns are viewable unless explicitly hidden).
 *
 * @param {string} tableName - Table key within `state.inputData.present.tableSettings`.
 * @param {string} columnName - Column key within the table's `isViewable` map.
 * @returns {(state: Object) => boolean} Selector returning whether the column is viewable.
 */
export const selectColumnIsViewable = (tableName, columnName) => (state) => {
  if (!tableName || typeof tableName !== 'string') {
    throw new Error(
      `selectColumnIsViewable: tableName must be a non-empty string (received: ${String(tableName)})`
    );
  }
  if (!columnName || typeof columnName !== 'string') {
    throw new Error(
      `selectColumnIsViewable: columnName must be a non-empty string (received: ${String(columnName)})`
    );
  }

  const tableSettings = state?.inputData?.present?.tableSettings?.[tableName];
  if (!tableSettings) {
    throw new Error(`selectColumnIsViewable: tableSettings not found for table "${tableName}"`);
  }

  const isViewableMap = tableSettings?.isViewable;
  if (!isViewableMap || typeof isViewableMap !== 'object') {
    throw new Error(`selectColumnIsViewable: isViewable config not found for table "${tableName}"`);
  }

  if (!(columnName in isViewableMap)) {
    return false;
  }

  const isViewable = isViewableMap[columnName];
  if (typeof isViewable !== 'boolean') {
    throw new Error(
      `selectColumnIsViewable: isViewable must be a boolean for "${tableName}.${columnName}" (received: ${typeof isViewable})`
    );
  }

  return isViewable;
};


/**
 * Creates a selector that returns a single cell value from a table row identified by UUID.
 *
 * Looks up the row in `state.inputData.present.data[tableName]` where `UUID === uuid`,
 * then returns the value at `columnName`.
 *
 * Notes:
 * - Throws for invalid arguments and missing/invalid table data to fail fast during development.
 * - Returns `undefined` if a matching row/column value cannot be found.
 *
 * @param {string} tableName - Table key within `state.inputData.present.data`.
 * @param {string} columnName - Column name to read from the matching row.
 * @param {string|number} uuid - Row identifier to match against the `UUID` column.
 * @returns {(state: Object) => any} Selector returning the cell value (or `undefined` if not found).
 */
export const selectDataTableValue = (tableName, columnName, uuid) => (state) => {
  if (!tableName || typeof tableName !== 'string') {
    throw new Error(
      `selectDataTableValue: tableName must be a non-empty string (received: ${String(tableName)})`
    );
  }

  if (!columnName || typeof columnName !== 'string') {
    throw new Error(
      `selectDataTableValue: columnName must be a non-empty string (received: ${String(columnName)})`
    );
  }

  if (uuid === null || uuid === undefined) {
    return null;
  }

  if (typeof uuid !== 'string' && typeof uuid !== 'number') {
    throw new Error(
      `selectDataTableValue: uuid must be a string, number, or null (received: ${String(uuid)})`
    );
  }

  const data = state?.inputData?.present?.data;
  if (!data || typeof data !== 'object') {
    throw new Error(`selectDataTableValue: state.inputData.present.data is missing or invalid`);
  }

  const table = data[tableName];
  if (!Array.isArray(table)) {
    throw new Error(
      `selectDataTableValue: invalid tableName "${tableName}" (table not found or not an array)`
    );
  }

  const res = inputFunctions.getValueAt(
    data,
    tableName,
    ['UUID'],
    [uuid],
    columnName
  );

  // Defensive: if getValueAt returns nothing (row not found, etc.), return undefined.
  return res?.value;
};


/**
 * Creates a selector that returns the "disabled settings" configuration for a given table
 * under the currently active model type.
 *
 * Disabled settings are defined in `model_configs.json` at:
 * `staticModelSettings[activeModelType].input.disabled[tableName]`
 *
 * Typical use case: hide/disable certain columns when switching model types
 * (e.g., a column like "Vehicle Type" may not apply for a given model).
 *
 * Behavior:
 * - Returns the disabled settings value for the table if configured.
 * - Returns `null` if:
 *   - there is no active model type,
 *   - the model has no `input.disabled` section,
 *   - the table has no disabled settings entry.
 *
 * Validation:
 * - Throws if `tableName` is not a non-empty string.
 * - Throws if `input.disabled` exists but is not an object (invalid config shape).
 *
 * @param {string} tableName - Table key to lookup in `input.disabled`.
 * @returns {(state: Object) => any|null} Selector returning disabled settings or `null`.
 */
export const selectDisabledTableSettings = (tableName) => (state) => {
  if (!tableName || typeof tableName !== 'string') {
    throw new Error(
      `selectDisabledTableSettings: tableName must be a non-empty string (received: ${String(tableName)})`
    );
  }

  const activeModelType = state?.inputData?.present?.activeModelType;
  if (!activeModelType) return null;

  const modelConfig = staticModelSettings?.[activeModelType];
  if (!modelConfig || typeof modelConfig !== 'object') return null;

  const disabledConfig = modelConfig?.input?.disabled;
  if (disabledConfig === undefined || disabledConfig === null) return null;

  if (typeof disabledConfig !== 'object') {
    throw new Error(
      `selectDisabledTableSettings: invalid config shape: staticModelSettings["${activeModelType}"].input.disabled must be an object`
    );
  }

  const tableDisabledSettings = disabledConfig?.[tableName];
  return tableDisabledSettings ?? null;
};


/**
 * Creates a selector that reads a table setting from `state.inputData.present.tableSettings`.
 *
 * Supports both direct settings and nested (sub) settings:
 * - `selectTableSetting('Trips', 'isViewable')`
 * - `selectTableSetting('Trips', 'isViewable', 'Vehicle Type')`
 *
 * Validation/behavior:
 * - `tableName` and `settingName` must be non-empty strings.
 * - `subSettingName` is optional; if provided it must be a non-empty string.
 * - Throws if `tableSettings`/table/setting/sub-setting are missing to fail fast on bad config/usage.
 *
 * @param {string} tableName - Table key within `state.inputData.present.tableSettings`.
 * @param {string} settingName - Setting key within the table settings object.
 * @param {string} [subSettingName] - Optional nested key within `tableSettings[tableName][settingName]`.
 * @returns {(state: Object) => any} Selector returning the requested setting value.
 */
export const selectTableSetting = (tableName, settingName, subSettingName) => (state) => {
  if (!tableName || typeof tableName !== 'string') {
    throw new Error(
      `selectTableSetting: tableName must be a non-empty string (received: ${String(tableName)})`
    );
  }

  if (!settingName || typeof settingName !== 'string') {
    throw new Error(
      `selectTableSetting: settingName must be a non-empty string (received: ${String(settingName)})`
    );
  }

  if (subSettingName !== undefined && subSettingName !== null) {
    if (typeof subSettingName !== 'string' || subSettingName.trim() === '') {
      throw new Error(
        `selectTableSetting: subSettingName must be a non-empty string when provided (received: ${String(subSettingName)})`
      );
    }
  }

  const tableSettingsRoot = state?.inputData?.present?.tableSettings;
  if (!tableSettingsRoot || typeof tableSettingsRoot !== 'object') {
    throw new Error(`selectTableSetting: state.inputData.present.tableSettings is missing or invalid`);
  }

  const tableSettings = tableSettingsRoot?.[tableName];
  if (!tableSettings || typeof tableSettings !== 'object') {
    throw new Error(`selectTableSetting: tableSettings not found for table "${tableName}"`);
  }

  // if (!(settingName in tableSettings)) {
  //   throw new Error(`selectTableSetting: setting "${settingName}" not found for table "${tableName}"`);
  // }

  const settingValue = tableSettings[settingName];

  if (subSettingName) {
    if (!settingValue || typeof settingValue !== 'object') {
      throw new Error(
        `selectTableSetting: setting "${tableName}.${settingName}" is not an object; cannot read subSetting "${subSettingName}"`
      );
    }

    if (!(subSettingName in settingValue)) {
      throw new Error(
        `selectTableSetting: subSetting "${tableName}.${settingName}.${subSettingName}" not found`
      );
    }

    return settingValue[subSettingName];
  }

  return settingValue;
};

/**
 * Creates a Redux selector that returns the settings object for a given table.
 *
 * @function selectTableSettings
 * @param {string} tableName - The key/name of the table whose settings should be selected.
 * @returns {(state: Object) => any} A selector function that reads `state.inputData.present.tableSettings[tableName]`.
 *
 * @description
 * This is a higher-order selector: call it with a `tableName` to get a selector function
 * that can be used with `useSelector` / `mapStateToProps`.
 *
 * @validation
 * - `tableName` must be a non-empty string.
 * - `state` must conform to the expected shape:
 *   `{ inputData: { present: { tableSettings: Record<string, any> } } }`.
 *
 * @throws {TypeError} If `tableName` is not a string or is an empty string (recommended validation).
 * @throws {TypeError} If the expected state path does not exist (will occur at runtime if state shape differs).
 */
export const selectTableSettings = (tableName) => state => {return state.inputData.present.tableSettings[tableName]};


/**
 * Creates a selector that builds dropdown option objects from a configured source column.
 *
 * The dropdown source is defined in `table_settings_static.json` at:
 * `STATIC_TABLE_SETTINGS[tableName].dropdownSource[columnName]` with shape:
 * `{ tableName: string, column: string }`
 *
 * Returned options have the shape: `{ value, id, label }` and are de-duplicated by `value`.
 *
 * Behavior:
 * - Throws if `tableName` / `columnName` are not non-empty strings.
 * - Returns `[]` if no dropdown source is configured for the provided table/column.
 * - Returns `[]` if the configured source table data is missing or not an array.
 *
 * @param {string} tableName - Table whose column is requesting dropdown values.
 * @param {string} columnName - Column within `tableName` whose dropdown source is configured.
 * @returns {(state: Object) => Array<{value: any, id: any, label: any}>} Selector returning unique dropdown options.
 */
export const getDropdownValuesFromColumn = (tableName, columnName) => (state) => {
  if (!tableName || typeof tableName !== 'string') {
    throw new Error(
      `getDropdownValuesFromColumn: tableName must be a non-empty string (received: ${String(tableName)})`
    );
  }

  if (!columnName || typeof columnName !== 'string') {
    throw new Error(
      `getDropdownValuesFromColumn: columnName must be a non-empty string (received: ${String(columnName)})`
    );
  }

  const dataRoot = state?.inputData?.present?.data;
  if (!dataRoot || typeof dataRoot !== 'object') {
    throw new Error(`getDropdownValuesFromColumn: state.inputData.present.data is missing or invalid`);
  }

  const dropdownSource = STATIC_TABLE_SETTINGS?.[tableName]?.dropdownSource?.[columnName];
  if (!dropdownSource) return [];

  const dropdownTableName = dropdownSource.tableName;
  const dataColumn = dropdownSource.column;

  if (!dropdownTableName || typeof dropdownTableName !== 'string') return [];
  if (!dataColumn || typeof dataColumn !== 'string') return [];

  const sourceTable = dataRoot?.[dropdownTableName];
  if (!Array.isArray(sourceTable) || sourceTable.length === 0) return [];

  const optionsByValue = new Map();

  for (const row of sourceTable) {
    const rawValue = row?.[dataColumn];
    if (rawValue === null || rawValue === undefined) continue;

    if (!optionsByValue.has(rawValue)) {
      optionsByValue.set(rawValue, { value: rawValue, id: rawValue, label: rawValue });
    }
  }

  return Array.from(optionsByValue.values());
};

/**
 * Creates a selector that returns dropdown option lists for each configured dropdown column in a table.
 *
 * Dropdown sources are defined in `table_settings_static.json` at:
 * `STATIC_TABLE_SETTINGS[tableName].dropdownSource`, shaped like:
 * ```js
 * {
 *   [targetColumnName]: {
 *     tableName: string,                 // source table
 *     column?: string,                   // (legacy) source column used for both key + display
 *     values?: Array<object>,            // (multi-update flag; selector still returns options for targetColumnName)
 *     keyFieldSource?: string,           // source column used as option.key when `values` exists
 *     displayField?: string              // source column used as option.display when `values` exists
 *   }
 * }
 * ```
 *
 * @param {string} tableName - Target table name whose dropdown config should be read.
 * @returns {(state: object) => Record<string, Array<{key: any, display: any}>>}
 * Selector returning `{ [columnName]: [{ key, display }, ...] }`.
 */
// One memoized selector per table name. The factory is called inline during
// render (`useSelector(selectTableDropdownValues(tableName))`), so returning a
// fresh closure each time would defeat memoization — the cache keeps a single
// createSelector instance per table. Memoization matters here because the
// result feeds DataEntryTable's `colDefs` memo and then ag-grid's `columnDefs`:
// an unstable reference made every mounted grid re-diff its entire column set
// on every store dispatch, including the solve-status poll every few seconds.
const tableDropdownValuesSelectorCache = new Map();

const NO_DROPDOWN_VALUES = Object.freeze({});

export const selectTableDropdownValues = (tableName) => {
  if (!tableName || typeof tableName !== 'string') {
    throw new Error(
      `selectTableDropdownValues: tableName must be a non-empty string (received: ${String(tableName)})`
    );
  }

  let selector = tableDropdownValuesSelectorCache.get(tableName);
  if (!selector) {
    selector = createSelector(
      [
        (state) => state?.inputData?.present?.data,
        (state) => state?.inputData?.present?.activeModelType ?? null,
      ],
      (dataRoot, activeModelType) => buildTableDropdownValues(tableName, dataRoot, activeModelType)
    );
    tableDropdownValuesSelectorCache.set(tableName, selector);
  }
  return selector;
};

function buildTableDropdownValues(tableName, dataRoot, activeModelType) {
  if (!dataRoot || typeof dataRoot !== 'object') {
    throw new Error(`selectTableDropdownValues: state.inputData.present.data is missing or invalid`);
  }

  const dropdownSources = STATIC_TABLE_SETTINGS?.[tableName]?.dropdownSource;
  if (!dropdownSources) return NO_DROPDOWN_VALUES;
  if (typeof dropdownSources !== 'object') {
    throw new Error(
      `selectTableDropdownValues: invalid config shape: STATIC_TABLE_SETTINGS["${tableName}"].dropdownSource must be an object`
    );
  }

  const dropdownValues = {};

  for (const [targetColumnName, dropdownSource] of Object.entries(dropdownSources)) {
    if (!dropdownSource || typeof dropdownSource !== 'object') {
      dropdownValues[targetColumnName] = [];
      continue;
    }

    const dropdownTableName = dropdownSource.tableName;
    if (!dropdownTableName || typeof dropdownTableName !== 'string') {
      dropdownValues[targetColumnName] = [];
      continue;
    }

    const usesMultiValues = Array.isArray(dropdownSource.values) && dropdownSource.values.length > 0;

    const keyField = usesMultiValues ? dropdownSource.keyFieldSource : dropdownSource.column;
    const displayField = usesMultiValues ? dropdownSource.displayField : dropdownSource.column;

    if (!keyField || typeof keyField !== 'string' || !displayField || typeof displayField !== 'string') {
      dropdownValues[targetColumnName] = [];
      continue;
    }

    const sourceTable = dataRoot[dropdownTableName];
    if (!Array.isArray(sourceTable) || sourceTable.length === 0) {
      dropdownValues[targetColumnName] = [];
      continue;
    }

    const filter = dropdownSource.filter ?? null;
    const filterApplies = filter && (
      !filter.modelTypes || filter.modelTypes.includes(activeModelType)
    );
    const filteredTable = filterApplies
      ? sourceTable.filter((row) => row?.[filter.field] === filter.value)
      : sourceTable;

    // A dropdown option needs a key to be selectable, so drop blank template
    // rows (e.g. unfilled vehicle slots) rather than offer them as options.
    const selectableRows = filteredTable.filter((row) => {
      const k = row?.[keyField];
      return k != null && k !== '';
    });

    // Optional positional fallback: when a source row has no display value (e.g.
    // vehicles whose name is optional and left blank), label it by position —
    // "Vehicle 1", "Vehicle 2" — so it's still identifiable and pickable rather
    // than being silently dropped by the grid's null-display filter.
    const indexLabel = dropdownSource.displayIndexLabel;
    dropdownValues[targetColumnName] = selectableRows.map((row, i) => {
      let display = row?.[displayField];
      if ((display == null || display === '') && indexLabel) {
        display = `${indexLabel} ${i + 1}`;
      }
      return { key: row?.[keyField], display };
    });
  }

  return dropdownValues;
}

/**
 * Creates a selector that returns the row values for a given UUID within a table.
 *
 * Behavior:
 * - If `uuid` is provided: returns the row (via `inputFunctions.getRowAt`) matching `UUID === uuid`.
 * - If `uuid` is not provided and `createNew === true`: returns a new "empty" row object containing
 *   only viewable, non-id, and not-hidden-on-data-entry columns initialized to `null`.
 * - If `uuid` is not provided and `createNew !== true`: returns `null`.
 *
 * Return shape:
 * - For "new row": `{ exists: false, value: Record<string, any> }`
 * - For existing rows: whatever `inputFunctions.getRowAt(...)` returns (expected to be `{ exists, value }`).
 *
 * @param {string} tableName - Table key within `state.inputData.present.data`.
 * @param {string|number|null|undefined} uuid - Row UUID to lookup (matches against the `UUID` column).
 * @param {boolean} [createNew=false] - Whether to create an empty row when `uuid` is not provided.
 * @returns {(state: object) => ({exists: boolean, value: any} | null)} Selector returning row lookup result or `null`.
 */
export const selectRow = (tableName, uuid, createNew = false) => (state) => {
  if (!tableName || typeof tableName !== 'string') {
    throw new Error(`selectRow: tableName must be a non-empty string (received: ${String(tableName)})`);
  }

  const hasUuid = uuid !== null && uuid !== undefined && uuid !== '';
  if (!hasUuid) {
    if (!createNew) return null;

    const tableSettings = state?.inputData?.present?.tableSettings?.[tableName];
    const columnNames = STATIC_TABLE_SETTINGS?.[tableName]?.columnName;

    if (!tableSettings || typeof tableSettings !== 'object') {
      throw new Error(`selectRow: tableSettings not found for table "${tableName}"`);
    }
    if (!Array.isArray(columnNames)) {
      throw new Error(`selectRow: STATIC_TABLE_SETTINGS["${tableName}"].columnName must be an array`);
    }

    const isViewable = tableSettings?.isViewable || {};
    const dataType = STATIC_TABLE_SETTINGS?.[tableName]?.dataType || {};

    const emptyRow = {};
    for (const col of columnNames) {
      if (
        isViewable?.[col] &&
        dataType?.[col] !== 'id'
      ) {
        emptyRow[col] = null;
      }
    }

    return { exists: false, value: emptyRow };
  }

  // Delegate to shared inputFunctions helper for consistent behavior.
  return inputFunctions.getRowAt(
    state?.inputData?.present?.data,
    tableName,
    ['UUID'],
    [uuid]
  );
};

/**
 * Creates a selector that returns the data array for a given input table.
 *
 * Behavior:
 * - Throws if `tableName` is not a non-empty string.
 * - Throws if `state.inputData.present.data` is missing/invalid.
 * - Returns the table array if found.
 * - Returns `[]` if the table is missing or is not an array.
 *
 * @param {string} tableName - Table key within `state.inputData.present.data`.
 * @returns {(state: Object) => any[]} Selector returning the table rows.
 */
export const selectTableData = (tableName) => (state) => {
  if (!tableName || typeof tableName !== 'string' || tableName.trim() === '') {
    throw new Error(
      `selectTableData: tableName must be a non-empty string (received: ${String(tableName)})`
    );
  }

  const dataRoot = state?.inputData?.present?.data;
  if (!dataRoot || typeof dataRoot !== 'object') {
    throw new Error(`selectTableData: state.inputData.present.data is missing or invalid`);
  }

  const table = dataRoot[tableName];
  // Stable fallback: a fresh `[]` here made every call a new reference for
  // missing tables, re-rendering equality-fn-less subscribers on every
  // dispatch (e.g. each solve-status poll).
  return Array.isArray(table) ? table : EMPTY_TABLE;
};


/**
 * Returns a selector function that determines if the specified table is an output table.
 *
 * @param {string} tableName - The name of the table to check in STATIC_TABLE_SETTINGS.
 * @returns {function(): boolean} A selector function that returns true if the table is an output table, otherwise false.
 */
export const selectIsResultsTable = (tableName) => () => {
  return STATIC_TABLE_SETTINGS[tableName].isResultTable
}


/**
 * Selector that returns the list of enabled *input* tables for the currently active model type.
 *
 * Excludes tables that are marked as `enabledOnlyWithOutput` in `model_configs.json`.
 *
 * Notes:
 * - Returns `[]` if there is no active model type or the model config is missing/malformed.
 * - Does not throw (intentionally) to keep selector usage resilient to partial state/config during initialization.
 *
 * @param {object} state - Redux state.
 * @returns {string[]} Array of active/enabled input table names.
 */
// Memoized: derived purely from static config keyed by the active model type,
// but it used to build a fresh array per call, re-rendering the paste/column-
// mapping buttons on every store dispatch.
export const selectActiveInputTables = createSelector(
  [(state) => state?.inputData?.present?.activeModelType],
  (activeModelType) => {
    if (!activeModelType) return EMPTY_TABLE;

    const modelConfig = staticModelSettings?.[activeModelType];
    const enabledMap = modelConfig?.input?.enabled;
    const enabledOnlyWithOutput = modelConfig?.input?.enabledOnlyWithOutput;

    if (!enabledMap || typeof enabledMap !== 'object') return EMPTY_TABLE;

    const tables = [];
    for (const [tableName, enabled] of Object.entries(enabledMap)) {
      if (!enabled) continue;
      if (enabledOnlyWithOutput?.[tableName]) continue;
      tables.push(tableName);
    }

    return tables;
  }
);


/**
 * Selector that determines whether the UI/form is allowed to add a new row to a given table.
 *
 * Current rule(s):
 * - For `trips`: at least one row must exist in the `locations` table before a trip can be added.
 *
 * Return shape:
 * - `{ valid: true }` when adding is allowed
 * - `{ valid: false, addFirst: string, message: string }` when blocked
 *
 * @param {string} tableName - Target table name to validate.
 * @returns {(state: object) => {valid: boolean, addFirst?: string, message?: string}}
 */
// Hoisted result objects so every call returns a stable reference per outcome —
// `{ valid: true }` rebuilt per call re-rendered the map-mode selector and the
// per-table "new entry" buttons on every store dispatch.
const CAN_ADD_ROW = Object.freeze({ valid: true });
const CANNOT_ADD_TRIP_NO_LOCATIONS = Object.freeze({
  valid: false,
  message: 'Unable to add a new trip until there is at least one entry in the Location table',
});
const CANNOT_ADD_TRIP_DISABLED = Object.freeze({
  valid: false,
  message: 'Unable to add a new trip because the trips input is not enabled for the active model type',
});

export const selectFormCanAddRow = (tableName) => (state) => {
  if (!tableName || typeof tableName !== 'string' || tableName.trim() === '') {
    throw new Error(
      `selectFormCanAddRow: tableName must be a non-empty string (received: ${String(tableName)})`
    );
  }

  const dataRoot = state?.inputData?.present?.data;
  if (!dataRoot || typeof dataRoot !== 'object') {
    throw new Error(`selectFormCanAddRow: state.inputData.present.data is missing or invalid`);
  }

  // Rule: Trips require at least one Location.
  if (tableName === 'trips') {
    const locations = dataRoot.locations;

    if (!Array.isArray(locations)) {
      throw new Error(
        `selectFormCanAddRow: expected state.inputData.present.data["locations"] to be an array`
      );
    }

    if (locations.length === 0) {
      return CANNOT_ADD_TRIP_NO_LOCATIONS;
    }
    const activeModelType = state?.inputData?.present?.["activeModelType"];
    const modelConfig = staticModelSettings?.[activeModelType];
    if (!modelConfig?.["input"]?.["enabled"]?.["trips"]) {
      return CANNOT_ADD_TRIP_DISABLED;
    }
  }

  return CAN_ADD_ROW;
};


/**
 * Selector that returns the number of rows in the active model's configured results table.
 *
 * Reads `state.inputData.present.activeModelType`, finds the model's `resultsTable` in
 * `model_configs.json`, then returns the length of `state.inputData.present.data[resultsTable]`.
 *
 * Behavior:
 * - Returns `0` if the active model type is not set, the model config is missing,
 *   `resultsTable` is not configured, or the results table data is missing/not an array.
 * - Throws only when the expected state shape is clearly invalid (missing `inputData.present`).
 *
 * @param {object} state - Redux state.
 * @returns {number} Number of rows in the active results table (or `0` when unavailable).
 */
export const selectActiveResultsShape = (state) => {
  const present = state?.inputData?.present;
  if (!present || typeof present !== 'object') {
    throw new Error('selectActiveResultsShape: expected state.inputData.present to exist');
  }

  const activeModelType = present.activeModelType;
  if (!activeModelType || typeof activeModelType !== 'string') return 0;

  const modelConfig = staticModelSettings?.[activeModelType];
  const resultsTable = modelConfig?.resultsTable;
  if (!resultsTable || typeof resultsTable !== 'string') return 0;

  const dataRoot = present.data;
  if (!dataRoot || typeof dataRoot !== 'object') return 0;

  const resultsData = dataRoot?.[resultsTable];
  return Array.isArray(resultsData) ? resultsData.length : 0;
};


export const selectResultSummary = (state) => {
  const present = state?.inputData?.present;
  if (!present || typeof present !== 'object') {
    throw new Error('selectResultSummary: expected state.inputData.present to exist');
  }

  // A summary is only meaningful alongside actual result rows. Saved scenarios
  // and example templates can carry a stale summary over an empty results table
  // (rows cleared or never populated, summary left behind) — which would light
  // up the Results Summary card while the Results card sits empty. Gate on the
  // active results table so the summary reads as absent whenever no results are
  // available, and after results are cleared.
  if (selectActiveResultsShape(state) === 0) return null;

  const resultSummary = present.data?.resultSummary;
  return resultSummary;
};


/**
 * Whether this schedule's travel times are straight-line estimates: 'none',
 * 'some' or 'all'. Memoized on the travel-cost table and the coordinate system,
 * so a component reading it subscribes to a string rather than rescanning an
 * O(N²) matrix on every render (1.65 ms at 250 locations, 6.6 ms at 500).
 *
 * Always 'none' on a plane: x-y distances are straight lines by definition, so
 * "not road-network times" would mean nothing there.
 */
export const selectTravelTimeEstimateLevel = createSelector(
  [
    (state) => state.inputData.present.data.travel_costs,
    (state) => state.inputData.present.generalSettings?.mapping?.coordinateSystem,
  ],
  (rows, coordinateSystem) => {
    if (coordinateSystem === 'cartesian') return 'none';
    const summary = travelTimeEstimateSummary(rows);
    if (!summary.some) return 'none';
    return summary.all ? 'all' : 'some';
  }
);
